import { ChangeDetectionStrategy, Component, inject, Input, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, Location } from '@angular/common';
import { RouterLink } from '@angular/router';

/**
 * Shared detail-page header: a back arrow plus an optional title/subtitle,
 * used across Scheduled Prompt pages (creator-view, invitee-view,
 * prompt-detail-view). The arrow navigates via [routerLink] (backLink) when
 * given, so it always lands on a known page instead of relying on browser
 * history — falls back to Location.back() when no backLink is supplied.
 */
@Component({
  selector: 'app-back-button',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './back-button.html',
  styleUrl: './back-button.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BackButton {
  /** Route to redirect to when the arrow is clicked, e.g. '/app/scheduleprompt'. */
  @Input() backLink: string | unknown[] | null = null;
  /** Page title shown next to the arrow, e.g. "Prompt Tracking: You". */
  @Input() title = '';
  /** Quoted prompt text shown in the subtitle, e.g. "Share a memory about your grandparents". */
  @Input() promptText = '';
  /** Category shown after the prompt text in the subtitle, e.g. "Family & Heritage". */
  @Input() categoryName = '';

  private readonly location = inject(Location);
  private readonly platformId = inject(PLATFORM_ID);
  readonly isBrowser = isPlatformBrowser(this.platformId);

  goBack(): void {
    if (this.isBrowser) this.location.back();
  }
}
